import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, isBackForwardNavigation } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 * @property {HTMLElement} menuIcon - Menu icon wrapper; `is-closing` plays the X → hamburger keyframes.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer', 'menuIcon'];
  /** @type {HTMLElement | null} */
  #customAnimationContainer = null;
  #customDrawerOffsetRaf = 0;
  #customDrawerOffsetPostRaf = 0;
  #boundUpdateCustomDrawerOffset = () => this.#updateCustomDrawerTopOffset();
  /** @type {ResizeObserver | null} */
  #flushBottomObserver = null;
  #flushBottomRaf = 0;
  /**
   * True while the main drawer is mid-close (`menu-open` removed, `open` still set until reset).
   * @type {boolean}
   */
  #isClosing = false;
  /** @type {AbortController | null} */
  #pageLifecycleController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#customAnimationContainer = this.closest('[data-custom-animation="true"]');

    this.#pageLifecycleController?.abort();
    this.#pageLifecycleController = new AbortController();
    const { signal } = this.#pageLifecycleController;
    window.addEventListener('pagehide', this.#onPageHide, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Native <details open> before the element upgraded: adopt so toggle/close stay owned.
    const details = this.refs.details;
    if (details?.hasAttribute('open') && !details.classList.contains('menu-open')) {
      details.classList.add('menu-open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      this.#scheduleCustomDrawerTopOffsetUpdate();
      this.#updateFlushBottomClass();
    }

    if (this.#customAnimationContainer) {
      this.#scheduleCustomDrawerTopOffsetUpdate();
      window.addEventListener('resize', this.#boundUpdateCustomDrawerOffset, { passive: true });
      this.#flushBottomObserver = new ResizeObserver(() => this.#scheduleFlushBottomUpdate());
      this.#flushBottomObserver.observe(this.refs.menuDrawer);
      const navigation = this.refs.menuDrawer.querySelector('.menu-drawer__navigation');
      if (navigation) this.#flushBottomObserver.observe(navigation);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#isClosing = false;

    this.#pageLifecycleController?.abort();
    this.#pageLifecycleController = null;

    if (this.#customDrawerOffsetRaf) {
      cancelAnimationFrame(this.#customDrawerOffsetRaf);
      this.#customDrawerOffsetRaf = 0;
    }

    if (this.#customDrawerOffsetPostRaf) {
      cancelAnimationFrame(this.#customDrawerOffsetPostRaf);
      this.#customDrawerOffsetPostRaf = 0;
    }

    if (this.#flushBottomObserver) {
      this.#flushBottomObserver.disconnect();
      this.#flushBottomObserver = null;
    }

    if (this.#flushBottomRaf) {
      cancelAnimationFrame(this.#flushBottomRaf);
      this.#flushBottomRaf = 0;
    }

    if (this.#customAnimationContainer) {
      window.removeEventListener('resize', this.#boundUpdateCustomDrawerOffset);
    }
  }

  #onPageHide = () => {
    this.#forceResetDrawer();
  };

  /**
   * Back / bfcache restore: never come back to a drawer left open by a link tap.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted && !isBackForwardNavigation()) return;

    this.#forceResetDrawer();
  };

  /** Snap the drawer and icon to closed with no animation. */
  #forceResetDrawer = () => {
    this.#isClosing = false;
    this.refs.menuIcon?.classList.remove('is-closing');

    this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach((element) => {
      if (element instanceof HTMLDetailsElement) reset(element);
    });
    reset(this.refs.details);
    this.refs.menuDrawer?.classList.remove('menu-drawer--has-submenu-opened', 'menu-drawer--flush-bottom');
    removeTrapFocus();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open or mid open/close transition
   * (`open` can linger after `menu-open` is removed during close).
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open') || this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer. Prevent native <details> toggle so open/close
   * stay owned by this component. Uses `menu-open` for intent — not the lingering
   * `open` attribute during close — and no-ops mid-close.
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault?.();
    if (this.#isClosing) return;
    if (this.refs.details.classList.contains('menu-open')) {
      return this.close();
    }
    return this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    event?.preventDefault?.();

    if (details === this.refs.details) {
      this.#isClosing = false;
      this.refs.menuIcon.classList.remove('is-closing');
    }

    details.setAttribute('open', '');
    this.#scheduleCustomDrawerTopOffsetUpdate();

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      this.#updateCustomDrawerTopOffset();
      details.classList.add('menu-open');

      if (details === this.refs.details) {
        this.#updateFlushBottomClass();
      }

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
        this.#updateFlushBottomClass();
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        trapFocus(details);
        this.#updateFlushBottomClass();
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isMain = details === this.refs.details;

    // Mid-close: ignore re-entry so backdrop/summary taps cannot restart the close.
    if (isMain && this.#isClosing) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (isMain) {
      this.#isClosing = true;
      this.refs.menuIcon.classList.add('is-closing');
      this.refs.menuDrawer.classList.remove('menu-drawer--flush-bottom');
    } else {
      this.#updateFlushBottomClass();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox.
    // Race a timeout so WebKit unfinished transitions cannot leave `#isClosing` stuck (toggle no-ops).
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    let settled = false;

    const finishClose = () => {
      if (settled) return;
      settled = true;

      reset(details);
      if (isMain) {
        this.#isClosing = false;
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    Promise.race([
      onAnimationEnd(drawer || details, undefined, { subtree: false }),
      new Promise((resolve) => window.setTimeout(resolve, 500)),
    ]).then(finishClose);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Measure once immediately, then again across the next paint frames so
   * sticky/header layout changes are reflected before first open.
   */
  #scheduleCustomDrawerTopOffsetUpdate() {
    if (!this.#customAnimationContainer) return;

    this.#updateCustomDrawerTopOffset();

    if (this.#customDrawerOffsetRaf) {
      cancelAnimationFrame(this.#customDrawerOffsetRaf);
    }

    if (this.#customDrawerOffsetPostRaf) {
      cancelAnimationFrame(this.#customDrawerOffsetPostRaf);
      this.#customDrawerOffsetPostRaf = 0;
    }

    this.#customDrawerOffsetRaf = requestAnimationFrame(() => {
      this.#customDrawerOffsetRaf = 0;
      this.#updateCustomDrawerTopOffset();

      this.#customDrawerOffsetPostRaf = requestAnimationFrame(() => {
        this.#customDrawerOffsetPostRaf = 0;
        this.#updateCustomDrawerTopOffset();
      });
    });
  }

  /**
   * Keep custom animated drawer aligned below the top header row.
   */
  #updateCustomDrawerTopOffset() {
    if (!this.#customAnimationContainer) return;

    const header = this.closest('.header') || this.ownerDocument.querySelector('#header-component .header');
    const topRow = header?.querySelector('.header__row--top');

    if (!(topRow instanceof HTMLElement)) {
      this.style.setProperty('--custom-mobile-drawer-top-offset', '0px');
      this.#updateFlushBottomClass();
      return;
    }

    const topRowRect = topRow.getBoundingClientRect();
    const topRowColumns = topRow.querySelector('.header__columns');
    const columnsBottom = topRowColumns instanceof HTMLElement ? topRowColumns.getBoundingClientRect().bottom : topRowRect.bottom;

    // Use the lowest visible edge and round up so the drawer always sits just below the row.
    const topOffset = Math.max(0, Math.ceil(Math.max(topRowRect.bottom, columnsBottom)));
    this.style.setProperty('--custom-mobile-drawer-top-offset', `${topOffset}px`);
    this.#updateFlushBottomClass();
  }

  /**
   * Coalesce flush-bottom checks onto the next frame after resize/open.
   */
  #scheduleFlushBottomUpdate() {
    if (this.#flushBottomRaf) cancelAnimationFrame(this.#flushBottomRaf);
    this.#flushBottomRaf = requestAnimationFrame(() => {
      this.#flushBottomRaf = 0;
      this.#updateFlushBottomClass();
    });
  }

  /**
   * Drop bottom corner radius when the open custom drawer content overflows its max-height.
   */
  #updateFlushBottomClass() {
    if (!this.#customAnimationContainer) return;

    const menuDrawer = this.refs.menuDrawer;
    if (!(menuDrawer instanceof HTMLElement)) return;

    if (!this.isOpen) {
      menuDrawer.classList.remove('menu-drawer--flush-bottom');
      return;
    }

    const navigation = menuDrawer.querySelector('.menu-drawer__navigation');
    const scrollEl = navigation instanceof HTMLElement ? navigation : menuDrawer;
    const drawerOverflows = menuDrawer.scrollHeight > menuDrawer.clientHeight + 1;
    const navOverflows = scrollEl.scrollHeight > scrollEl.clientHeight + 1;
    menuDrawer.classList.toggle('menu-drawer--flush-bottom', drawerOverflows || navOverflows);
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
